import * as React from "react";
import SideNavigation from "@amzn/awsui-components-react/polaris/side-navigation";
import Header from "@amzn/awsui-components-react/polaris/header";
import Box from "@amzn/awsui-components-react/polaris/box";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import Alert from "@amzn/awsui-components-react/polaris/alert";
import PageShell from "../../../../global/components/PageShell";
import { readHash, setHash, syncHash, initHashNav } from "../../../../pages/utils/hashNav";
import InitiateProgramEnrollmentSection from "./initiate_enrollment/InitiateProgramEnrollmentSection";
import InitiateAndActivateProgramEnrollmentSection from "./initiate_and_activate_enrollment/InitiateAndActivateProgramEnrollmentSection";
import ActivateProgramEnrollmentSection from "./activate_enrollment/ActivateProgramEnrollmentSection";
import GetProgramEnrollmentByCidSection from "./get_program_enrollment_by_cid/GetProgramEnrollmentByCidSection";
import GetSchedulesByContractIdSection from "./get_schedules_by_contract/GetSchedulesByContractIdSection";
import ExpeditePacketsOrderSection from "./expedite_packets_order/ExpeditePacketsOrderSection";
import ReplanSchedulesSection from "./replan_schedules/ReplanSchedulesSection";
import CancelPacketsEnrollmentSection from "./cancel_packets_enrollment/CancelPacketsEnrollmentSection";
import AddMedToPacketsSection from "./add_med_to_packets/AddMedToPacketsSection";
import RemoveMedFromPacketsSection from "./remove_med_from_packets/RemoveMedFromPacketsSection";
import RphDecisionApprovalSection from "./rph_decision/RphDecisionApprovalSection";
import UpdateBenefitDatesSection from "./update_benefit_dates/UpdateBenefitDatesSection";
import UpdateNextBillableDateSection from "./update_nbd/UpdateNextBillableDateSection";

// Single source of truth for all PacMan operations.
// Add new operations here — sidebar nav and rendering all derive from this.
const OPERATIONS = {
  "initiate-enrollment": { component: InitiateProgramEnrollmentSection },
  "activate-enrollment": { component: ActivateProgramEnrollmentSection },
  "initiate-and-activate-enrollment": { component: InitiateAndActivateProgramEnrollmentSection },
  "get-schedules-by-contract": { component: GetSchedulesByContractIdSection },
  "get-enrollment-by-cid": { component: GetProgramEnrollmentByCidSection },
  "expedite-packets-order": { component: ExpeditePacketsOrderSection },
  "replan-schedules": { component: ReplanSchedulesSection },
  "cancel-packets-enrollment": { component: CancelPacketsEnrollmentSection },
  "add-med-to-packets": { component: AddMedToPacketsSection },
  "remove-med-from-packets": { component: RemoveMedFromPacketsSection },
  "rph-decision-approval": { component: RphDecisionApprovalSection },
  "update-benefit-dates": { component: UpdateBenefitDatesSection },
  "update-next-billable-date": { component: UpdateNextBillableDateSection },
};

type PacManOperation = keyof typeof OPERATIONS;

interface State {
  navOpen: boolean;
  activePage: PacManOperation;
}

class PacManPage extends React.Component<{}, State> {
  private cleanupHashNav: (() => void) | null = null;

  constructor(props: {}) {
    super(props);

    const hash = readHash();
    const activePage: PacManOperation = isValidOperation(hash) ? hash : "initiate-enrollment";

    this.state = {
      navOpen: true,
      activePage,
    };
  }

  componentDidMount() {
    syncHash(this.state.activePage);
    this.cleanupHashNav = initHashNav((hash) => {
      if (isValidOperation(hash)) {
        this.setState({ activePage: hash });
      }
    });
  }

  componentWillUnmount() {
    this.cleanupHashNav?.();
  }

  render() {
    const { navOpen, activePage } = this.state;

    return (
      <PageShell
        navigationOpen={navOpen}
        onNavigationChange={(open) => this.setState({ navOpen: open })}
        navigation={
          <SideNavigation
            header={{ text: "PacMan", href: "#initiate-enrollment" }}
            activeHref={`#${activePage}`}
            onFollow={(e) => {
              e.preventDefault();
              const page = decodeURIComponent(e.detail.href.replace(/^#/, "")) as PacManOperation;
              this.setState({ activePage: page });
              setHash(page);
            }}
            items={[
              {
                type: "section",
                text: "Enrollment",
                items: [
                  { type: "link", text: "Initiate Program Enrollment", href: "#initiate-enrollment" },
                  { type: "link", text: "Activate Program Enrollment", href: "#activate-enrollment" },
                  { type: "link", text: "Initiate and Activate Enrollment", href: "#initiate-and-activate-enrollment" },
                  { type: "link", text: "Cancel Packets Enrollment", href: "#cancel-packets-enrollment" },
                ],
              },
              {
                type: "section",
                text: "Orders & Schedules",
                items: [
                  { type: "link", text: "Get Schedules by ContractId", href: "#get-schedules-by-contract" },
                  { type: "link", text: "Get Enrollment by Customer ID", href: "#get-enrollment-by-cid" },
                  { type: "link", text: "Expedite Packets Order", href: "#expedite-packets-order" },
                  { type: "link", text: "Replan Schedules", href: "#replan-schedules" },
                ],
              },
              {
                type: "section",
                text: "Medications",
                items: [
                  { type: "link", text: "Add Med to Packets", href: "#add-med-to-packets" },
                  { type: "link", text: "Remove Med from Packets", href: "#remove-med-from-packets" },
                ],
              },
              {
                type: "section",
                text: "Other",
                items: [
                  { type: "link", text: "RPH Decision Approval", href: "#rph-decision-approval" },
                  { type: "link", text: "Update Benefit Dates", href: "#update-benefit-dates" },
                  { type: "link", text: "Update Next Billable Date", href: "#update-next-billable-date" },
                ],
              },
            ]}
          />
        }
        content={
          <div>
            <Box padding={{ top: "l", bottom: "l" }}>
              <Header variant="h1">PacMan</Header>
            </Box>
            <SpaceBetween size="l">
              <Alert type="info">
                For more information on how to use this page please visit our{" "}
                <a
                  href="https://w.amazon.com/bin/view/Pharmacy/PacManPortalAPIs/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Wiki page
                </a>
                !
              </Alert>
              {this.renderContent()}
            </SpaceBetween>
          </div>
        }
      />
    );
  }

  private renderContent() {
    const Component = OPERATIONS[this.state.activePage].component;
    return <Component />;
  }
}

function isValidOperation(value: string): value is PacManOperation {
  return value in OPERATIONS;
}

export default PacManPage;
